// A row under a finger (SwipeActions.swift, ListScreen.swift): sideways it follows the finger over a tile that names
// what letting go does (left: schedule; right: move); held still for a moment, it lifts and can be dragged. A long
// swipe to the left deletes nothing on letting go: the row stays open on a Delete button beside Schedule, and a tap on
// it deletes (docs/59, 7a). A tap elsewhere, a scroll or a swipe back closes it; one row is open at a time. A tap
// still opens a row (the click), and up and down is the page's scroll.

import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { beginTouchDrag, endTouchDrag, moveTouchDrag } from "./dnd";

export type SwipeAction = "schedule" | "move" | "delete";

export interface SwipeState {
  action: SwipeAction | null;
  armed: boolean;
  /** Open on its buttons (Schedule and Delete) after a long swipe to the left. */
  open: boolean;
}

const THRESHOLD = 80;
/** Of the finger's travel: past the first threshold the row follows more slowly. */
const FAR = 230;
/** How far an open row stands to the left: Schedule (64), Delete (92), the gaps and the edge (as on the iPhone). */
export const OPEN = 172;
const HOLD_MS = 450;
const SLOP = 10;

/** The one row that stands open, if any: opening another closes it. */
let openRow: { element: HTMLElement; close: () => void } | null = null;

/** Closes the open row, if there is one (after one of its buttons was used). */
export function closeOpenRow(): void {
  openRow?.close();
}

export function useRowTouch(row: RefObject<HTMLElement | null>, options: {
  enabled: boolean;
  canDrag: boolean;
  /** Whether the row swipes to the left at all (a habit's day without Not Today has nothing there). */
  swipesLeft?: boolean;
  /** Whether it swipes at all (a habit's line in Habits › All only lifts to be dragged); sideways is then a scroll. */
  swipes?: boolean;
  dragIDs: () => readonly string[];
  onSwipeState: (state: SwipeState) => void;
  onSwipe: (action: Exclude<SwipeAction, "delete">) => void;
}): void {
  const { enabled } = options;
  // The latest options without re-attaching the listeners on every render.
  const latest = useRef(options);
  useLayoutEffect(() => {
    latest.current = options;
  });
  useEffect(() => {
    const element = row.current;
    if (!element || !enabled) return;
    // "away": the finger came down on a button or a field, which handle it themselves.
    let mode: "none" | "away" | "swipe" | "scroll" | "drag" = "none";
    let start = { x: 0, y: 0 };
    let hold: ReturnType<typeof setTimeout> | undefined;
    let state: SwipeState = { action: null, armed: false, open: false };
    /** Where the row stood when the finger came down, and where it stands now. */
    let base = 0;
    let offset = 0;

    const report = (next: SwipeState) => {
      if (next.action !== state.action || next.armed !== state.armed || next.open !== state.open) {
        latest.current.onSwipeState(next);
      }
      state = next;
    };
    const setOffset = (value: number) => {
      offset = value;
      element.style.setProperty("--swipe", `${value}px`);
      element.style.setProperty("--swipe-width", `${Math.max(Math.abs(value) - 6, 0)}px`);
    };
    const settle = (value: number) => {
      element.classList.add("settling");
      element.classList.remove("swiping");
      setOffset(value);
      setTimeout(() => element.classList.remove("settling"), 250);
    };

    // A touch anywhere else closes the open row; it still does what it does there.
    const outside = (event: TouchEvent) => {
      if (!element.contains(event.target as Node)) close();
    };
    const close = () => {
      if (!state.open) return;
      element.classList.remove("swiped-open");
      settle(0);
      report({ action: null, armed: false, open: false });
      if (openRow?.element === element) openRow = null;
      document.removeEventListener("touchstart", outside, true);
    };
    const open = () => {
      if (openRow && openRow.element !== element) openRow.close();
      openRow = { element, close };
      element.classList.add("swiped-open");
      settle(-OPEN);
      report({ action: "delete", armed: true, open: true });
      document.addEventListener("touchstart", outside, true);
      // A light tick: nothing has been done yet.
      navigator.vibrate?.(8);
    };

    const begin = (event: TouchEvent) => {
      if (event.touches.length !== 1 || (event.target as HTMLElement).closest("button, input, textarea")) {
        mode = "away";
        return;
      }
      const touch = event.touches[0]!;
      start = { x: touch.clientX, y: touch.clientY };
      mode = "none";
      base = state.open ? -OPEN : 0;
      if (latest.current.canDrag && !state.open) {
        hold = setTimeout(() => {
          if (mode !== "none") return;
          mode = "drag";
          navigator.vibrate?.(10);
          beginTouchDrag(latest.current.dragIDs(), element, start.x, start.y);
        }, HOLD_MS);
      }
    };
    const move = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (mode === "none") {
        if (Math.hypot(dx, dy) > SLOP / 2) clearTimeout(hold);
        if (Math.abs(dx) > SLOP && Math.abs(dx) > Math.abs(dy) && latest.current.swipes === false) {
          mode = "scroll";
        } else if (Math.abs(dx) > SLOP && Math.abs(dx) > Math.abs(dy)) {
          mode = "swipe";
          element.classList.add("swiping");
        } else if (Math.abs(dy) > SLOP) {
          mode = "scroll";
          close();
        }
      }
      if (mode === "drag") {
        event.preventDefault();
        moveTouchDrag(touch.clientX, touch.clientY);
      } else if (mode === "swipe" && state.open) {
        // An open row follows the finger back; further to the left it only gives a little.
        event.preventDefault();
        setOffset(dx < 0 ? base + dx * 0.2 : Math.min(base + dx, 0));
      } else if (mode === "swipe") {
        event.preventDefault();
        if (dx < 0 && latest.current.swipesLeft === false) {
          setOffset(0);
          report({ action: null, armed: false, open: false });
          return;
        }
        const distance = Math.abs(dx);
        const eased = distance > THRESHOLD ? THRESHOLD + (distance - THRESHOLD) * 0.3 : distance;
        setOffset(dx < 0 ? -eased : eased);
        const armed = distance > THRESHOLD;
        const action: SwipeAction = dx > 0 ? "move" : armed && distance > FAR ? "delete" : "schedule";
        if (armed && !state.armed) navigator.vibrate?.(8);
        report({ action, armed, open: false });
      }
    };
    const end = (event: TouchEvent) => {
      clearTimeout(hold);
      if (mode === "drag") {
        // No click after a hold: the row was lifted, not tapped.
        event.preventDefault();
        endTouchDrag(event.type === "touchend");
      } else if (state.open) {
        if (mode === "none") {
          // A tap on an open row closes it rather than opening the to-do.
          event.preventDefault();
          close();
        } else if (mode === "swipe") {
          // Back more than half way closes; otherwise it settles open again.
          if (offset > -OPEN / 2) close();
          else settle(-OPEN);
        }
      } else if (mode === "swipe") {
        const chosen = state.armed && event.type === "touchend" ? state.action : null;
        if (chosen === "delete") {
          open();
        } else {
          settle(0);
          report({ action: null, armed: false, open: false });
          if (chosen) latest.current.onSwipe(chosen);
        }
      }
      mode = "none";
    };
    element.addEventListener("touchstart", begin, { passive: true });
    element.addEventListener("touchmove", move, { passive: false });
    element.addEventListener("touchend", end);
    element.addEventListener("touchcancel", end);
    return () => {
      clearTimeout(hold);
      // Taken away while the row is in the hand (a card opened, the list changed): its touchend no longer reaches
      // here, so the drag ends now, or the copy in the hand and the lifted row would stay.
      if (mode === "drag") endTouchDrag(false);
      close();
      element.removeEventListener("touchstart", begin);
      element.removeEventListener("touchmove", move);
      element.removeEventListener("touchend", end);
      element.removeEventListener("touchcancel", end);
    };
  }, [row, enabled, latest]);
}
